import { v4 as uuid } from "uuid";

import constants from "utils/constants";

import { Features } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import {
    ScheduleEvent,
    ScheduleEventSubtype,
    ScheduleEventType,
    ScheduleIntervalType,
    SchedulerTime,
} from "store/environments/tenants/tenant-scheduler/tenant-scheduler-types";

import { TableItem } from "./Table/Table";

export interface SchedulerTab {
    key: string;
    type: ScheduleEventType;
    subtype: ScheduleEventSubtype;
    /**
     * Shows the Type and Interval columns, so the schedule can also run every N minutes.
     */
    isExtended: boolean;
    isVisible?: (features: Features) => boolean;
}

export const schedulerTabs: ReadonlyArray<SchedulerTab> = [
    {
        key: "email",
        type: ScheduleEventType.Email,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: false,
    },
    {
        key: "vendorPrepaymentCheck",
        type: ScheduleEventType.VendorPrepaymentRegisterCheck,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: true,
    },
    {
        key: "invoiceWaitingForPr",
        type: ScheduleEventType.InvoiceWaitingForPrCheck,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: true,
    },
    {
        key: "mlModelReTraining",
        type: ScheduleEventType.MachineLearningModelReTraining,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: false,
    },
    {
        key: "poOnHoldMonthly",
        type: ScheduleEventType.Email,
        subtype: ScheduleEventSubtype.OnHoldMonthlyInvoiceEmail,
        isExtended: false,
    },
    {
        key: "onHoldDueDate",
        type: ScheduleEventType.Email,
        subtype: ScheduleEventSubtype.OnHoldDueDateInvoiceEmail,
        isExtended: false,
    },
    {
        key: "recurringInvoices",
        type: ScheduleEventType.RecurringInvoices,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: true,
        isVisible: (features) => features.recurringInvoicesEnabled,
    },
    {
        key: "invoiceEscalation",
        type: ScheduleEventType.InvoiceEscalation,
        subtype: ScheduleEventSubtype.OpenInvoiceEmail,
        isExtended: true,
    },
];

export const defaultSchedulerTab = schedulerTabs[0];

export const getTabLabelKey = (tab: SchedulerTab): string =>
    `tenantConfig.scheduler.navBar.${tab.key}`;

export const isTabVisible = (tab: SchedulerTab, features: Features | null) =>
    !tab.isVisible || (features !== null && tab.isVisible(features));

export const isMachineLearningEnabled = (features: Features | null) =>
    features !== null &&
    (features.codingPredictionEnabled ||
        features.salesTaxPredictionEnabled ||
        features.workflowPredictionEnabled);

export const weekdayValues: ReadonlyArray<number> = [1, 2, 4, 8, 16, 32, 64];

export const allWeekdays = weekdayValues.reduce((sum, value) => sum + value, 0);

export const defaultTimeOfDay = "10:00:00";
export const defaultInterval = 1;

export const createScheduleTime = (
    tab: SchedulerTab
): TableItem<SchedulerTime> =>
    tab.type === ScheduleEventType.RecurringInvoices
        ? {
              key: uuid(),
              intervalInMinutes: 10,
              intervalType: ScheduleIntervalType.Minutes,
              lastStarted: null,
              timeOfDay: null,
              weekdays: allWeekdays,
          }
        : {
              key: uuid(),
              intervalInMinutes: null,
              intervalType: ScheduleIntervalType.TimeOfDay,
              lastStarted: null,
              timeOfDay: defaultTimeOfDay,
              weekdays: weekdayValues[0],
          };

export const toTableItem = (time: SchedulerTime): TableItem<SchedulerTime> => ({
    ...time,
    key: time.id ?? uuid(),
});

export const toSchedulerTime = (
    item: TableItem<SchedulerTime>
): SchedulerTime => ({
    id: item.id,
    intervalInMinutes: item.intervalInMinutes,
    intervalType: item.intervalType,
    lastStarted: item.lastStarted,
    timeOfDay: item.timeOfDay,
    weekdays: item.weekdays,
});

export const getSelectedWeekdays = (weekdays: number | null): number[] =>
    weekdayValues.filter((value) => ((weekdays ?? 0) & value) === value);

export const combineWeekdays = (values: ReadonlyArray<number>): number =>
    values.reduce((sum, value) => sum + value, 0);

/**
 * The time picker needs a full date; the day itself is irrelevant and never shown.
 */
const timePickerDate = "2000-01-01";

export const parseTimeOfDay = (timeOfDay: string | null): Date | null =>
    timeOfDay ? new Date(`${timePickerDate}T${timeOfDay}`) : null;

export const formatTimeOfDay = (date: Date): string =>
    date.toLocaleTimeString(constants.localizations.us, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

/**
 * The schedule runs in UTC, so "now" has to be the current UTC wall-clock time.
 */
export const getCurrentUtcTime = (): Date => {
    const now = new Date();

    return new Date(
        `${timePickerDate}T${String(now.getUTCHours()).padStart(2, "0")}:${String(
            now.getUTCMinutes()
        ).padStart(2, "0")}:00`
    );
};

export type ScheduleEventForm = Omit<ScheduleEvent, "scheduleTimes"> & {
    scheduleTimes: TableItem<SchedulerTime>[];
};

export const toScheduleEventForm = (
    scheduleEvent: ScheduleEvent | null,
    tab: SchedulerTab
): ScheduleEventForm => {
    const form: ScheduleEventForm = scheduleEvent
        ? {
              ...scheduleEvent,
              scheduleTimes: scheduleEvent.scheduleTimes.map(toTableItem),
          }
        : {
              enabled: false,
              event: tab.type,
              subtype: tab.subtype,
              scheduleTimes: [],
          };

    // An enabled schedule without a single time never runs, so give the user a row to fill.
    if (form.enabled && form.scheduleTimes.length === 0) {
        form.scheduleTimes = [createScheduleTime(tab)];
    }

    return form;
};

export const toScheduleEvent = (form: ScheduleEventForm): ScheduleEvent => ({
    ...form,
    scheduleTimes: form.enabled ? form.scheduleTimes.map(toSchedulerTime) : [],
});

/**
 * Returns `undefined` when the typed value must be ignored, e.g. it has too many digits.
 */
export const parseIntervalInput = (
    value: string | number | null
): number | null | undefined => {
    if (value === null || value === "") {
        return null;
    }

    if (String(value).length > constants.maxSchedulerIntervalDigitsLimit) {
        return undefined;
    }

    const parsed = Number(value);

    return Number.isNaN(parsed) ? null : parsed;
};
